// ═══════════════════════════════════════════════════════════════════════
// crossRefs.js — Cross-tab entity navigation
// ═══════════════════════════════════════════════════════════════════════
// Single registry mapping the four major entity types in this app:
//   · Interfaces (IF-001 … IF-040)         live in: Tracker, LLD, Contracts, DFD, Architecture
//   · Agents (AG-001 … AG-027)              live in: AgenticAI tab
//   · Benefits (B-01 … B-76)                live in: Benefits tab
//   · ESB Tier-1 Flows (F1 … F6)            live in: ESB tab
//   · Human Gates (G-01 … G-12)             live in: AgenticAI tab
//
// Every entry of every map is reviewed-by-hand against the slidedecks.
// ═══════════════════════════════════════════════════════════════════════

// ─── 1.  AGENT  →  list of interface IDs it operates on  ──────────────
//
// Driven by the agent's `tools` array and zone scope from agenticAIData.js
// cross-referenced with the actual IFACES list.
//
// Conventions used:
//   PLM agents work on ECN, BOM, item-master, CAD interfaces
//   ERP agents work on order, material-master, AFRU, period-close interfaces
//   MES agents work on work-order, operation, NCR, dispatch interfaces
//   SFC agents work on telemetry, edge, tag-mapping, anomaly interfaces
//   QUAL agents work on NCR, CAPA, audit, SPC interfaces
//   CLOUD agents work on schedule, forecast, KPI, knowledge interfaces
//   B2B agents work on partner-onboarding, DLQ, SLA interfaces

export const AGENT_TO_INTERFACES = {
  // PLM
  "AG-001": ["IF-001", "IF-002", "IF-003", "IF-035"],          // Design Assistant
  "AG-002": ["IF-001", "IF-002"],                               // ECN Triage
  "AG-003": ["IF-002", "IF-003", "IF-004"],                     // BOM Reconciler
  "AG-004": ["IF-001", "IF-003", "IF-035"],                     // CAD Steward

  // ERP
  "AG-005": ["IF-014", "IF-015", "IF-016"],                     // Order Orchestration
  "AG-006": ["IF-013", "IF-014", "IF-027"],                     // Variance Analyst
  "AG-007": ["IF-005", "IF-013"],                               // MDM Steward
  "AG-008": ["IF-027", "IF-028"],                               // Period Close

  // MES
  "AG-009": ["IF-015", "IF-016", "IF-017"],                     // Dispatch
  "AG-010": ["IF-006", "IF-017"],                               // WI Generator
  "AG-011": ["IF-007", "IF-008", "IF-018"],                     // NCR Triage
  "AG-012": ["IF-017", "IF-018"],                               // Andon Triage
  "AG-013": ["IF-007", "IF-018"],                               // Containment

  // SFC + Edge
  "AG-014": ["IF-020", "IF-021", "IF-022"],                     // Anomaly Detector
  "AG-015": ["IF-020", "IF-022"],                               // Tag Mapping
  "AG-016": ["IF-021", "IF-022"],                               // Energy Optimizer
  "AG-017": ["IF-020", "IF-021"],                               // Maintenance Predictor

  // Quality
  "AG-018": ["IF-007", "IF-008"],                               // CAPA Drafter
  "AG-019": ["IF-008", "IF-019"],                               // SPC Analyst
  "AG-020": ["IF-007", "IF-008", "IF-019"],                     // Audit Preparer

  // Cloud / Analytics
  "AG-021": ["IF-016", "IF-024", "IF-025"],                     // Schedule Optimizer
  "AG-022": ["IF-014", "IF-024"],                               // Demand Sensor
  "AG-023": ["IF-023", "IF-024", "IF-025"],                     // Knowledge Bot
  "AG-024": ["IF-022", "IF-024"],                               // KPI Custodian

  // B2B / IT Ops — only if user has B2B in scope. Excluded by design here.
  "AG-025": [],                                                  // DLQ Triage
  "AG-026": [],                                                  // Partner Onboarder
  "AG-027": [],                                                  // SLA Watchdog
};

// ─── 2.  BENEFIT  →  list of interface IDs that deliver it  ────────────
//
// For each of the 76 benefits, which interfaces materially contribute?
// Only filled for benefits with clear interface ownership; "felt-only"
// benefits (culture, trust) intentionally have no link.

export const BENEFIT_TO_INTERFACES = {
  // Engineering & Design
  "B-01": ["IF-001", "IF-002", "IF-003"],          // ECN cycle-time
  "B-02": ["IF-002", "IF-003", "IF-004"],          // BOM accuracy
  "B-03": ["IF-001", "IF-035"],                     // Re-use rate
  "B-04": ["IF-001", "IF-035"],                     // CAD-to-MBOM
  "B-05": ["IF-001", "IF-002"],                     // Effectivity precision
  "B-06": [],                                        // Knowledge retention (felt)
  "B-07": ["IF-003", "IF-004"],                     // D2M handoff

  // Manufacturing Execution
  "B-08": ["IF-022", "IF-024"],                     // OEE
  "B-09": ["IF-008", "IF-019"],                     // FPY
  "B-10": ["IF-017", "IF-018", "IF-022"],          // Floor visibility
  "B-11": ["IF-017", "IF-022"],                     // Cycle-time variance
  "B-12": ["IF-016", "IF-017"],                     // Changeover
  "B-13": ["IF-016", "IF-017"],                     // Schedule adherence
  "B-14": ["IF-007", "IF-022", "IF-019"],          // Genealogy
  "B-15": ["IF-018"],                                // Andon response
  "B-16": [],                                        // Operator confidence (felt)

  // Transactional & Finance
  "B-17": ["IF-027"],                                // AFRU accuracy
  "B-18": ["IF-013", "IF-027"],                     // Inventory accuracy
  "B-19": ["IF-014", "IF-027"],                     // Working capital
  "B-20": ["IF-027", "IF-028"],                     // COGS accuracy
  "B-21": ["IF-013", "IF-014"],                     // MRP signal
  "B-22": ["IF-027", "IF-028"],                     // Period close

  // Quality
  "B-23": ["IF-007", "IF-008"],                     // NCR closure
  "B-24": ["IF-008", "IF-019"],                     // Inspection coverage
  "B-25": ["IF-007", "IF-008", "IF-019"],          // COPQ
  "B-26": ["IF-007", "IF-008", "IF-019"],          // Audit readiness
  "B-27": ["IF-007", "IF-008"],                     // Closed-loop quality

  // Supply Chain & B2B  (excluded from scope)
  "B-28": [], "B-29": [], "B-30": [], "B-31": [], "B-32": [],

  // Workforce
  "B-33": ["IF-006", "IF-017"],                     // Operator training
  "B-34": [], "B-35": [],                            // Tribal-knowledge, empowerment (felt)
  "B-36": [],                                        // Skills coverage (mostly HR)
  "B-37": [], "B-38": [],                            // (felt)

  // Data, Analytics & Decisions
  "B-39": ["IF-022", "IF-024"],                     // KPI freshness
  "B-40": ["IF-005", "IF-013"],                     // MDM consistency
  "B-41": ["IF-020", "IF-022"],                     // Anomaly detection
  "B-42": ["IF-014", "IF-024"],                     // Forecast accuracy
  "B-43": [],                                        // Evidence-based decisions (felt)

  // Integration & Architecture
  "B-44": ["IF-029", "IF-030"],                     // New-plant integration
  "B-45": ["IF-029", "IF-030", "IF-031"],          // Backbone availability
  "B-46": ["IF-031"],                                // MTTR
  "B-47": [], "B-48": [],                            // unit cost / debt (felt)
  "B-49": ["IF-031"],                                // Observability/RCA

  // Customer Experience
  "B-50": ["IF-014", "IF-015"],                     // Order visibility
  "B-51": ["IF-015", "IF-016"],                     // Delivery reliability
  "B-52": ["IF-008", "IF-019"],                     // Quality consistency
  "B-53": ["IF-001", "IF-014"],                     // Configurability

  // Sustainability & ESG
  "B-54": ["IF-022"],                                // Energy/unit
  "B-55": ["IF-008", "IF-019"],                     // Scrap reduction
  "B-56": ["IF-022", "IF-027"],                     // Carbon reporting
  "B-57": [],                                        // Recycled-content (felt)

  // Risk & Resilience
  "B-58": ["IF-007", "IF-018"],                     // Recall scope
  "B-59": ["IF-029", "IF-031"],                     // SPOF reduction
  "B-60": ["IF-029", "IF-030"],                     // Cyber surface
  "B-61": ["IF-007", "IF-008", "IF-019"],          // Regulatory
  "B-62": [],                                        // Supply resilience (excluded)

  // Strategic
  "B-63": ["IF-001", "IF-002", "IF-035"],          // TTM
  "B-64": ["IF-029", "IF-030"],                     // M&A
  "B-65": ["IF-022", "IF-024"],                     // Servitization
  "B-66": [], "B-67": [],                            // Brand, talent (felt)

  // Cultural — all felt
  "B-68": [], "B-69": [], "B-70": [], "B-71": [], "B-72": [],

  // Knowledge & Improvement
  "B-73": [], "B-74": [],                            // (felt)
  "B-75": [], "B-76": ["IF-019"],                   // Audit improvement loop
};

// ─── 3.  ESB Tier-1 FLOW  →  interface IDs  ────────────────────────────

export const FLOW_TO_INTERFACES = {
  "F1": ["IF-001", "IF-002", "IF-035"],            // ECN propagation
  "F2": ["IF-002", "IF-003", "IF-004"],            // BOM publish
  "F3": ["IF-014", "IF-015", "IF-016"],            // Work order release
  "F4": ["IF-017", "IF-027"],                       // Production confirmation
  "F5": ["IF-007", "IF-018"],                       // NCR + Containment
  "F6": ["IF-020", "IF-022", "IF-024"],            // OEE / Telemetry
};

// Human-readable flow metadata (consumed by ESB tab + DFD)
export const FLOWS = [
  { id: "F1", name: "ECN Propagation",          tier: 1, route: "TC → OPC + SAP + IX",       sla: "P99 < 90s", color: "#ff4f8b" },
  { id: "F2", name: "BOM Publish",              tier: 1, route: "TC → SAP + OPC",            sla: "P99 < 5min", color: "#c084fc" },
  { id: "F3", name: "Work Order Release",       tier: 1, route: "SAP/APS → OPC EX",          sla: "P99 < 30s",  color: "#ffb300" },
  { id: "F4", name: "Production Confirm",       tier: 1, route: "OPC EX → SAP + IX",         sla: "P99 < 30s · exactly-once", color: "#fb923c" },
  { id: "F5", name: "NCR + Containment",        tier: 1, route: "OPC EX → TC + IX",          sla: "P99 < 60s · regulated",    color: "#ff4f8b" },
  { id: "F6", name: "OEE / Telemetry",          tier: 2, route: "Edge → IX + OPC",           sla: "P99 < 5s · at-least-once", color: "#00d4c0" },
];

// ─── 4.  HUMAN GATE  →  interfaces it protects  ────────────────────────

export const GATE_TO_INTERFACES = {
  1:  ["IF-001", "IF-002", "IF-035"],              // Safety-critical chars
  2:  ["IF-002", "IF-003"],                         // BOM structure changes
  3:  ["IF-001", "IF-002"],                         // Effectivity scope
  4:  ["IF-007", "IF-008"],                         // CAPA closure
  5:  ["IF-027", "IF-028"],                         // Financial postings
  6:  ["IF-005", "IF-013"],                         // MDM create + merge
  7:  ["IF-014", "IF-015"],                         // Customer comms
  8:  ["IF-019"],                                    // Regulatory submissions
  9:  ["IF-005"],                                    // Supplier qualification
  10: ["IF-029", "IF-030"],                         // Cybersec policy
  11: ["IF-016"],                                    // Cross-plant resource
  12: [],                                            // Agent autonomy (meta)
};

// ─── 5.  Reverse maps · computed once at import ────────────────────────

// Interface ID → agent IDs that touch it
export const INTERFACE_TO_AGENTS = {};
for (const [agentId, ifaceIds] of Object.entries(AGENT_TO_INTERFACES)) {
  for (const ifaceId of ifaceIds) {
    if (!INTERFACE_TO_AGENTS[ifaceId]) INTERFACE_TO_AGENTS[ifaceId] = [];
    INTERFACE_TO_AGENTS[ifaceId].push(agentId);
  }
}

// Interface ID → benefit IDs it delivers
export const INTERFACE_TO_BENEFITS = {};
for (const [benefitId, ifaceIds] of Object.entries(BENEFIT_TO_INTERFACES)) {
  for (const ifaceId of ifaceIds) {
    if (!INTERFACE_TO_BENEFITS[ifaceId]) INTERFACE_TO_BENEFITS[ifaceId] = [];
    INTERFACE_TO_BENEFITS[ifaceId].push(benefitId);
  }
}

// Interface ID → ESB flow IDs it participates in
export const INTERFACE_TO_FLOWS = {};
for (const [flowId, ifaceIds] of Object.entries(FLOW_TO_INTERFACES)) {
  for (const ifaceId of ifaceIds) {
    if (!INTERFACE_TO_FLOWS[ifaceId]) INTERFACE_TO_FLOWS[ifaceId] = [];
    INTERFACE_TO_FLOWS[ifaceId].push(flowId);
  }
}

// Interface ID → gate numbers
export const INTERFACE_TO_GATES = {};
for (const [gateNum, ifaceIds] of Object.entries(GATE_TO_INTERFACES)) {
  for (const ifaceId of ifaceIds) {
    if (!INTERFACE_TO_GATES[ifaceId]) INTERFACE_TO_GATES[ifaceId] = [];
    INTERFACE_TO_GATES[ifaceId].push(Number(gateNum));
  }
}

// ─── 6.  Convenience getters  ──────────────────────────────────────────

export const getAgentsForInterface  = (ifaceId)   => INTERFACE_TO_AGENTS[ifaceId]   || [];
export const getBenefitsForInterface = (ifaceId)  => INTERFACE_TO_BENEFITS[ifaceId] || [];
export const getFlowsForInterface    = (ifaceId)  => INTERFACE_TO_FLOWS[ifaceId]    || [];
export const getGatesForInterface    = (ifaceId)  => INTERFACE_TO_GATES[ifaceId]    || [];
export const getInterfacesForAgent   = (agentId)  => AGENT_TO_INTERFACES[agentId]   || [];
export const getInterfacesForBenefit = (benefitId)=> BENEFIT_TO_INTERFACES[benefitId]|| [];
export const getInterfacesForFlow    = (flowId)   => FLOW_TO_INTERFACES[flowId]     || [];
export const getInterfacesForGate    = (gateNum)  => GATE_TO_INTERFACES[gateNum]    || [];

// ─── 7.  Interface ↔ B2MML schema/contract mapping  ─────────────────────
//
// Maps each interface to the B2MML schema(s) it relates to.
// Derived from the federation contracts in b2mmlData.js.
//
// Pattern: an interface gets a B2MML link when its semantic role matches
// a federation contract schema (e.g., production order interfaces map to
// OperationsSchedule; material movement interfaces to Material).

export const INTERFACE_TO_B2MML_SCHEMAS = {
  // ECN / ECO / BOM (Engineering changes) — not directly B2MML, but the
  // downstream effectivity changes propagate via Material + ProcessSegment
  "IF-001": ["MATERIAL", "PROCESS_SEGMENT"],
  "IF-002": ["MATERIAL", "PROCESS_SEGMENT"],
  "IF-003": ["MATERIAL", "PROCESS_SEGMENT"],
  "IF-004": ["MATERIAL"],

  // Master data — Material + Equipment
  "IF-005": ["MATERIAL", "EQUIPMENT"],
  "IF-012": ["MATERIAL"],
  "IF-013": ["MATERIAL"],
  "IF-026": ["MATERIAL", "PROCESS_SEGMENT"],
  "IF-032": ["MATERIAL"],
  "IF-035": ["PROCESS_SEGMENT", "OPERATIONS_DEFINITION"],

  // Production orders & schedules — OperationsSchedule
  "IF-014": ["OPERATIONS_SCHEDULE"],
  "IF-015": ["OPERATIONS_DEFINITION", "OPERATIONS_SCHEDULE"],
  "IF-016": ["OPERATIONS_SCHEDULE", "OPERATIONS_CAPABILITY"],
  "IF-017": ["OPERATIONS_PERFORMANCE"],

  // Operation confirmations / actuals — OperationsPerformance
  "IF-027": ["OPERATIONS_PERFORMANCE"],
  "IF-028": ["OPERATIONS_PERFORMANCE"],

  // Quality / NCR — Material lots + OperationsPerformance
  "IF-007": ["MATERIAL", "OPERATIONS_PERFORMANCE"],
  "IF-008": ["MATERIAL", "OPERATIONS_PERFORMANCE"],
  "IF-019": ["OPERATIONS_PERFORMANCE"],

  // SFC / dispatch — OperationsSchedule (the dispatch list)
  "IF-018": ["OPERATIONS_SCHEDULE"],

  // Equipment state / telemetry — Equipment (limited B2MML; mostly OPC UA)
  "IF-020": ["EQUIPMENT"],
  "IF-021": ["EQUIPMENT"],
  "IF-022": ["EQUIPMENT", "OPERATIONS_PERFORMANCE"],

  // Personnel / operator assignment
  "IF-006": ["PERSONNEL"],

  // Everything else (telemetry / cloud KPIs / B2B / etc.) — no direct B2MML
};

export const getB2MMLSchemasForInterface = (ifaceId) => INTERFACE_TO_B2MML_SCHEMAS[ifaceId] || [];
